import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl, defineMessages } from 'react-intl-next';
import ImageLoader from 'react-render-image';

import Image from '@atlaskit/image';
import { Box, Flex, Pressable, Stack, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import LinkBrokenIcon from '@atlaskit/icon/core/link-broken';
import CrossIcon from '@atlaskit/icon/core/cross';
import { IconButton } from '@atlaskit/button/new';

import { expValEquals } from '@confluence/feature-experiments';

import smartlinkAnimation from './assets/smartlink animation.gif';

const messages = defineMessages({
	nudgeTitle: {
		id: 'experiment-smartlink-auth-nudge.nudge.title',
		defaultMessage:
			'This page has {count} {providerName} {count, plural, one {link} other {links}}',
		description:
			'Title of the nudge shown when scrolling on a page with unauthenticated smart links.',
	},
	nudgeDescription: {
		id: 'experiment-smartlink-auth-nudge.nudge.description',
		defaultMessage: "Connect to preview what's inside.",
		description:
			'Description for unauthenticated smart links nudge informing the user they can connect smart links.',
	},
	linkOverflowText: {
		id: 'experiment-smartlink-auth-nudge.nudge.link-overflow-text',
		defaultMessage: '+{count} more',
		description: 'Represents the number of links that are not shown.',
	},
	connectButton: {
		id: 'experiment-smartlink-auth-nudge.nudge.connect-button',
		defaultMessage: 'Connect',
		description: 'Connect button label informing the user they can connect a smart link provider.',
	},
	connectedButton: {
		id: 'experiment-smartlink-auth-nudge.nudge.connected-button',
		defaultMessage: 'Connected',
		description:
			'Connected button label informing the user they successfully connected a smart link provider.',
	},
	nudgeTitleSuccess: {
		id: 'experiment-smartlink-auth-nudge.nudge.title-success',
		defaultMessage: ' {providerName} connected',
		description:
			'Title of the nudge shown when scrolling on a page with unauthenticated smart links once the smart link provider has successfully connected.',
	},
	nudgeDescriptionSuccess: {
		id: 'experiment-smartlink-auth-nudge.nudge.description-success',
		defaultMessage: ' Links now display as synced previews',
		description:
			'Description for unauthenticated smart links nudge informing the user the smart links are now synced previews.',
	},
});

const MAX_VISIBLE = 3;
const MAX_CHARS = 25;
const DOMAIN_STRIP_REGEX = /^.*?\.com\//;

const truncateUrl = (url: string): string => {
	const simplified = url.replace(DOMAIN_STRIP_REGEX, '');
	return simplified.length > MAX_CHARS ? `${simplified.slice(0, MAX_CHARS)}...` : simplified;
};

// @TODO: replace with real data from useUnauthSmartlinks
const MOCK_TOP_PROVIDER_LINK_NAMES = [
	'google.com/abc',
	'google.com/abcdefghijklmnopqrstuvwxyzover25characters',
	'google.com/over25charactersabcdefghijklmnopqrstuvwxyz',
	'google.com/overflowlinknoshow',
	'google.com/donotshowlink',
];

const styles = cssMap({
	contentContainer: {
		padding: token('space.200'),
	},
	titleRow: {
		alignItems: 'center',
		gap: token('space.100'),
	},
	borderedBox: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
	},
	providerName: {
		flex: 1,
	},
	providerRow: {
		paddingInline: token('space.150'),
		paddingTop: token('space.150'),
		alignItems: 'center',
		gap: token('space.100'),
	},
	linkNames: {
		paddingInlineEnd: token('space.150'),
		paddingBottom: token('space.150'),
		// icon width (12px) + gap (space.100 = 8px) + providerRow paddingInline (space.150 = 12px) = 32px
		paddingInlineStart: token('space.400'),
	},
	linkNameItem: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		color: token('color.text.subtlest'),
		font: token('font.body.small'),
	},
	nudgeBox: {
		position: 'fixed',
		bottom: token('space.400'),
		left: token('space.400'),
		width: '360px',
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
		backgroundColor: token('elevation.surface.overlay'),
		overflow: 'hidden',
		zIndex: 500,
		transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
	},
	headerImageStyles: {
		width: '100%',
		height: '100%',
		objectFit: 'cover',
	},
	imageContainer: {
		position: 'relative',
		width: '100%',
		overflow: 'hidden',
	},
	dismissButtonBox: {
		position: 'absolute',
		top: token('space.100'),
		right: token('space.100'),
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small'),
	},
	connectButton: {
		backgroundColor: token('elevation.surface.overlay'),
		border: 'none',
		padding: token('space.0'),
		color: token('color.link'),
		cursor: 'pointer',
		font: token('font.body'),
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
			color: token('color.link.pressed'),
		},
	},
});

export const SmartlinkAuthNudge = () => {
	const intl = useIntl();
	const hasScrolled = useRef(false);
	const hasShown = useRef(false);
	const [isVisible, setIsVisible] = useState(false);
	const [isConnected, setIsConnected] = useState(false);

	const handleConnect = useCallback(async () => {
		// @TODO: update with topProvider logic once eligibility hook is ready
		setIsConnected(true);
	}, []);

	const handleDismiss = useCallback(() => {
		setIsVisible(false);
	}, []);

	// If the user already scrolled before loading finished, show now
	useEffect(() => {
		// @TODO: add !isLoading and topProvider check once useUnauthSmartlinks hook is connected and add to dependency array
		if (hasScrolled.current && !hasShown.current) {
			hasShown.current = true;
			setIsVisible(true);
		}
	}, []);

	useEffect(() => {
		const scrollContainer = document.getElementById('AkMainContent') ?? document;

		const handleScroll = () => {
			hasScrolled.current = true;
			// @TODO: add !isLoading and topProvider check once useUnauthSmartlinks hook is connected and add to dependency array
			if (!hasShown.current) {
				hasShown.current = true;
				setIsVisible(true);
			}
		};

		scrollContainer.addEventListener('scroll', handleScroll, { once: true });

		return () => {
			scrollContainer.removeEventListener('scroll', handleScroll);
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// @TODO: update with real data when unAuthSmartlinks is ready
	const topProviderCount = 1;
	const topProviderName = 'Google Drive';
	const topProviderIconUrl = '';
	const overflowCount = MOCK_TOP_PROVIDER_LINK_NAMES.length - MAX_VISIBLE;
	const truncatedLinks = useMemo(
		() => MOCK_TOP_PROVIDER_LINK_NAMES.slice(0, MAX_VISIBLE).map(truncateUrl),
		[],
	);

	// @TODO: add an or check with !topProvider, so typeof document === 'undefined' || !topProvider
	if (typeof document === 'undefined') {
		return null;
	}

	const nudgeContent = expValEquals('cc_smartlink_auth_nudge_exp', 'isEnabled', true) ? (
		<Box
			xcss={styles.nudgeBox}
			style={{
				transform: isVisible ? 'translateY(0)' : 'translateY(120%)',
				opacity: isVisible ? 1 : 0,
			}}
		>
			<Box xcss={styles.imageContainer}>
				{/* @TODO: replace with real gifs later */}
				<Image src={smartlinkAnimation} alt="" css={styles.headerImageStyles} />
				<Box xcss={styles.dismissButtonBox}>
					<IconButton icon={CrossIcon} label="Dismiss" onClick={handleDismiss} />
				</Box>
			</Box>
			<Stack xcss={styles.contentContainer} space="space.200">
				<Flex xcss={styles.titleRow}>
					<LinkBrokenIcon label="" />
					<Text weight="bold" color="color.text">
						{intl.formatMessage(messages.nudgeTitle, {
							count: topProviderCount,
							providerName: topProviderName,
						})}
					</Text>
				</Flex>
				<Text color="color.text.subtle">{intl.formatMessage(messages.nudgeDescription)}</Text>
				<Stack xcss={styles.borderedBox} space="space.100">
					<Flex xcss={styles.providerRow}>
						{topProviderIconUrl ? (
							<ImageLoader
								src={topProviderIconUrl}
								loaded={
									<img src={topProviderIconUrl} alt={topProviderName} width={16} height={16} />
								}
								errored={<LinkBrokenIcon label="" size="small" />}
							/>
						) : (
							<LinkBrokenIcon label="" size="small" />
						)}
						<Box xcss={styles.providerName}>
							<Text weight="semibold">{topProviderName}</Text>
						</Box>
						<Pressable xcss={styles.connectButton} onClick={handleConnect} isDisabled={isConnected}>
							{isConnected
								? intl.formatMessage(messages.connectedButton)
								: intl.formatMessage(messages.connectButton)}
						</Pressable>
					</Flex>
					{truncatedLinks.length > 0 && (
						<Stack xcss={styles.linkNames} space="space.100">
							{truncatedLinks.map((name, i) => (
								<Box key={i} xcss={styles.linkNameItem}>
									{name}
								</Box>
							))}
							{overflowCount > 0 && (
								<Box xcss={styles.linkNameItem}>
									{intl.formatMessage(messages.linkOverflowText, { count: overflowCount })}
								</Box>
							)}
						</Stack>
					)}
				</Stack>
			</Stack>
		</Box>
	) : null;

	return nudgeContent ? createPortal(nudgeContent, document.body) : null;
};
